/**
 * SchedulingEventTypesCompact - Lista compacta dos Event Types cadastrados
 * Exibe na sidebar do calendário com barra de cor, título, duração e badge ativo/inativo
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-14 v1.0.0 - Implementação inicial
 */
import React from 'react';
import { CalendarDays, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useSchedulingEventTypes } from '@/hooks/useSchedulingEventTypes';
import { cn } from '@/lib/utils';
import { stripTemplateVariables } from '@/lib/schedulingUtils';

/**
 * Card compacto listando os Event Types cadastrados
 */
export function SchedulingEventTypesCompact() {
  const { eventTypes, isLoading } = useSchedulingEventTypes();
  const navigate = useNavigate();

  // Navega para a página de configuração de agendamento
  const handleGoToSettings = () => {
    navigate('/tenant/scheduling');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-2">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-10 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2 px-4 pt-4 flex flex-row items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Tipos de Evento
          </CardTitle>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleGoToSettings}
          title="Gerenciar tipos de evento"
        >
          <ExternalLink className="h-3 w-3 text-muted-foreground" />
        </Button>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {eventTypes.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-xs text-muted-foreground">
              Nenhum tipo de evento cadastrado
            </p>
            <Button
              variant="link"
              size="sm"
              className="mt-1 text-xs h-auto p-0"
              onClick={handleGoToSettings}
            >
              Criar tipo de evento
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {eventTypes.map(et => (
              <div
                key={et.id}
                className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors cursor-pointer"
                onClick={handleGoToSettings}
              >
                {/* Barra de cor lateral */}
                <div
                  className="w-1 h-8 rounded-full shrink-0"
                  style={{ backgroundColor: et.color || 'hsl(var(--primary))' }}
                />
                {/* Título e duração */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">
                    {stripTemplateVariables(et.title)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {et.duration_minutes} min
                  </p>
                </div>
                {/* Badge ativo/inativo */}
                <Badge
                  variant={et.is_active ? 'default' : 'secondary'}
                  className={cn(
                    'text-[9px] px-1.5 py-0',
                    et.is_active ? '' : 'opacity-60'
                  )}
                >
                  {et.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
